/* =============================================================
   Header — United Dream Mongolia (Simplified)
   Core actions: Виз мэдүүлэх | Зөвлөгөө авах
   ============================================================= */
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingCart, MessageCircle } from "lucide-react";

import { useLocation } from "wouter";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [, navigate] = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const coreActions = [
    {
      id: "visa",
      label: "Виз мэдүүлэх",
      icon: <ShoppingCart className="w-4 h-4" />,
      onClick: () => { setMobileOpen(false); navigate("/"); },
      style: "bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-700 hover:to-indigo-700 shadow-md shadow-violet-500/30",
    },
    {
      id: "consult",
      label: "Зөвлөгөө авах",
      icon: <MessageCircle className="w-4 h-4" />,
      onClick: () => { setMobileOpen(false); navigate("/consultation-request"); },
      style: "border border-white/20 text-white/80 hover:text-white hover:border-white/40 hover:bg-white/5",
    },
  ];

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-[#0D0A2E]/95 backdrop-blur-md shadow-lg shadow-violet-900/30" : "bg-[#0D0A2E]/80 backdrop-blur-sm"
        }`}
      >
        <div className="container">
          <div className="flex items-center justify-between h-16 md:h-18">

            {/* Logo */}
            <a href="/" onClick={(e) => { e.preventDefault(); navigate("/"); }}
              className="flex items-center gap-2.5 group flex-shrink-0">
              <div className="w-9 h-9 rounded-lg overflow-hidden shadow-md shadow-violet-500/40 group-hover:shadow-violet-500/60 transition-shadow">
                <img src="/logo-64.png" alt="United Dream Mongolia" className="w-full h-full object-cover" />
              </div>
              <div className="leading-none">
                <div className="text-white text-[1.05rem] tracking-tight" style={{ fontFamily: "var(--font-display)", fontWeight: 800, letterSpacing: "-0.02em" }}>
                  United Dream
                </div>
                <div style={{ fontSize: "0.6rem", color: "#a78bfa", fontFamily: "var(--font-display)", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                  Mongolia
                </div>
              </div>
            </a>

            {/* Desktop core action buttons */}
            <nav className="hidden md:flex items-center gap-2">
              {coreActions.map((action) => (
                <button
                  key={action.id}
                  onClick={action.onClick}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${action.style}`}
                  style={{ fontFamily: "var(--font-display)", fontSize: "0.82rem" }}
                >
                  {action.icon}
                  {action.label}
                </button>
              ))}
            </nav>

            {/* Mobile toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2 text-white/80 hover:text-white transition-colors"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="fixed top-0 right-0 bottom-0 z-50 w-72 bg-[#0D0A2E] shadow-2xl md:hidden flex flex-col"
            >
              {/* Mobile header */}
              <div className="flex items-center justify-between p-5 border-b border-white/10">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg overflow-hidden">
                    <img src="/logo-64.png" alt="Logo" className="w-full h-full object-cover" />
                  </div>
                  <span className="text-white" style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "0.95rem" }}>
                    United Dream
                  </span>
                </div>
                <button onClick={() => setMobileOpen(false)} className="p-1.5 text-white/60 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Mobile core action buttons */}
              <nav className="flex flex-col p-5 gap-3 flex-1">
                <p className="text-white/40 text-xs uppercase tracking-widest mb-1" style={{ fontFamily: "var(--font-display)" }}>
                  Үйлчилгээ
                </p>
                {coreActions.map((action, i) => (
                  <motion.button
                    key={action.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.07 }}
                    onClick={action.onClick}
                    className={`flex items-center gap-3 px-4 py-3.5 rounded-xl text-left font-semibold transition-all ${action.style}`}
                    style={{ fontFamily: "var(--font-display)", fontSize: "0.9rem" }}
                  >
                    <span className="flex-shrink-0">{action.icon}</span>
                    {action.label}
                  </motion.button>
                ))}
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
